// src/utils/model_diff.js
//
// Turns two model rows (as returned by GET /model/list) into a git-style diff.
//
// The important part is toCanonicalText(). Diffing JSON.stringify output
// directly is useless here: prompt content is a single enormous line with
// escaped "\n", so changing one word renders as a whole-line replace. We render
// each model to a normalized text form with real newlines first, then diff that
// line by line.
//
// Pure functions only, no Vue. ModelDiffView.vue renders what buildHunks
// returns; ModelConfigPanel.vue reuses the same path for its pre-save diff.
import { diffLines, diffWordsWithSpace } from "diff";

// Fields that describe how the model behaves. Identity and state columns
// (id, name, group_id, activated, created_at) are deliberately excluded: they
// always differ between two versions and say nothing about the configuration.
const META_FIELDS = ["family", "api_based", "owner", "description"];

// Preferred ordering for the keys inside one prompt entry. Anything else is
// appended alphabetically, so an unexpected key still shows up in the diff.
const PROMPT_KEY_ORDER = ["role", "content", "content_variables"];

function orderedKeys(obj, preferred = []) {
  const keys = Object.keys(obj || {});
  const known = preferred.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !preferred.includes(k)).sort();
  return [...known, ...rest];
}

// Render one value into the body of a canonical-text section.
function renderValue(value) {
  if (value === null || value === undefined) return "(none)";
  if (typeof value === "string") return value === "" ? "(empty)" : value;
  if (typeof value === "boolean" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.length ? value.map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join(", ") : "(none)";
  }
  return JSON.stringify(sortKeysDeep(value), null, 2);
}

// Stable key ordering so the raw-JSON view never reports a difference that is
// only key insertion order.
export function sortKeysDeep(value) {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, k) => {
        acc[k] = sortKeysDeep(value[k]);
        return acc;
      }, {});
  }
  return value;
}

// Model -> normalized text. Sections are headed by a bracketed path so a diff
// hunk always carries enough context to tell you which prompt you are looking
// at, and so an added or removed prompt key shows as a clean block.
export function toCanonicalText(model, { includeMeta = true } = {}) {
  if (!model) return "";
  const out = [];

  if (includeMeta) {
    out.push("[meta]");
    for (const field of META_FIELDS) {
      out.push(`${field}: ${renderValue(model[field])}`);
    }
    out.push("");
  }

  const prompts = model.prompts || {};
  for (const promptKey of Object.keys(prompts).sort()) {
    const entry = prompts[promptKey];
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      for (const field of orderedKeys(entry, PROMPT_KEY_ORDER)) {
        out.push(`[prompts.${promptKey}.${field}]`);
        out.push(renderValue(entry[field]));
        out.push("");
      }
    } else {
      out.push(`[prompts.${promptKey}]`);
      out.push(renderValue(entry));
      out.push("");
    }
  }

  const parameters = model.parameters || {};
  out.push("[parameters]");
  const paramKeys = Object.keys(parameters).sort();
  if (!paramKeys.length) {
    out.push("(none)");
  } else {
    for (const key of paramKeys) {
      const value = parameters[key];
      const rendered =
        value && typeof value === "object" ? JSON.stringify(sortKeysDeep(value)) : renderValue(value);
      out.push(`${key}: ${rendered}`);
    }
  }

  return out.join("\n");
}

// Model -> pretty-printed JSON of the comparable fields only. Useful when you
// want to see structural changes (a prompt key added or removed) rather than
// prose changes.
export function toRawJsonText(model, { includeMeta = true } = {}) {
  if (!model) return "";
  const subset = {};
  if (includeMeta) {
    for (const field of META_FIELDS) subset[field] = model[field];
  }
  subset.prompts = model.prompts || {};
  subset.parameters = model.parameters || {};
  return JSON.stringify(sortKeysDeep(subset), null, 2);
}

// Split a jsdiff part into lines without inventing a trailing empty line for
// the usual "ends with \n" case.
function partLines(value) {
  const lines = value.split("\n");
  if (lines.length && lines[lines.length - 1] === "") lines.pop();
  return lines;
}

// Word-level refinement for a removed/added line pair. Returns null when the
// two lines are too dissimilar to be a meaningful edit of one another, in which
// case the view shows them as a plain delete plus a plain add.
const REFINE_SIMILARITY_FLOOR = 0.35;

function refinePair(oldText, newText) {
  if (!oldText || !newText) return null;
  const parts = diffWordsWithSpace(oldText, newText);
  let unchanged = 0;
  let total = 0;
  for (const part of parts) {
    total += part.value.length;
    if (!part.added && !part.removed) unchanged += part.value.length;
  }
  if (total === 0 || unchanged / total < REFINE_SIMILARITY_FLOOR) return null;
  return {
    oldSegments: parts.filter((p) => !p.added).map((p) => ({ value: p.value, changed: !!p.removed })),
    newSegments: parts.filter((p) => !p.removed).map((p) => ({ value: p.value, changed: !!p.added })),
  };
}

// Flat line records with both line numberings attached, before hunking.
function toRows(leftText, rightText) {
  const parts = diffLines(leftText, rightText);
  const rows = [];
  let oldNo = 0;
  let newNo = 0;

  for (const part of parts) {
    for (const text of partLines(part.value)) {
      if (part.added) {
        rows.push({ type: "add", oldNo: null, newNo: ++newNo, text, segments: null });
      } else if (part.removed) {
        rows.push({ type: "del", oldNo: ++oldNo, newNo: null, text, segments: null });
      } else {
        rows.push({ type: "context", oldNo: ++oldNo, newNo: ++newNo, text, segments: null });
      }
    }
  }
  return rows;
}

// Pair each delete run with the add run that immediately follows it and refine
// the pairs word by word. Mutates the rows in place.
function refineRows(rows) {
  let i = 0;
  while (i < rows.length) {
    if (rows[i].type !== "del") {
      i += 1;
      continue;
    }
    let delEnd = i;
    while (delEnd < rows.length && rows[delEnd].type === "del") delEnd += 1;
    let addEnd = delEnd;
    while (addEnd < rows.length && rows[addEnd].type === "add") addEnd += 1;

    const pairs = Math.min(delEnd - i, addEnd - delEnd);
    for (let k = 0; k < pairs; k += 1) {
      const delRow = rows[i + k];
      const addRow = rows[delEnd + k];
      const refined = refinePair(delRow.text, addRow.text);
      if (refined) {
        delRow.segments = refined.oldSegments;
        addRow.segments = refined.newSegments;
      }
    }
    i = addEnd > delEnd ? addEnd : delEnd;
  }
  return rows;
}

// Group changed rows into hunks with `context` lines of surrounding context,
// merging changes that are closer together than 2 * context.
function toHunks(rows, context) {
  const changed = rows.map((r) => r.type !== "context");
  const ranges = [];

  for (let i = 0; i < rows.length; i += 1) {
    if (!changed[i]) continue;
    const start = Math.max(0, i - context);
    let end = i;
    while (end + 1 < rows.length && changed[end + 1]) end += 1;
    const stop = Math.min(rows.length - 1, end + context);
    const last = ranges[ranges.length - 1];
    if (last && start <= last.stop + 1) {
      last.stop = Math.max(last.stop, stop);
    } else {
      ranges.push({ start, stop });
    }
    i = end;
  }

  return ranges.map(({ start, stop }) => {
    const slice = rows.slice(start, stop + 1);
    const oldLines = slice.filter((r) => r.oldNo !== null);
    const newLines = slice.filter((r) => r.newNo !== null);
    const oldStart = oldLines.length ? oldLines[0].oldNo : 0;
    const newStart = newLines.length ? newLines[0].newNo : 0;
    return {
      header: `@@ -${oldStart},${oldLines.length} +${newStart},${newLines.length} @@`,
      rows: slice,
    };
  });
}

// Without a trailing newline on both sides, jsdiff sees "last line" and
// "last line\n" as different, so appending to a file reports the previous last
// line as changed too. partLines() drops the empty tail this introduces.
function withTrailingNewline(text) {
  if (!text) return "";
  return text.endsWith("\n") ? text : `${text}\n`;
}

// Main entry point. Returns { hunks, stats, identical }.
export function buildHunks(leftText, rightText, { context = 3 } = {}) {
  const left = withTrailingNewline(leftText);
  const right = withTrailingNewline(rightText);
  if (left === right) {
    return { hunks: [], stats: { added: 0, removed: 0 }, identical: true };
  }

  const rows = refineRows(toRows(left, right));
  const stats = rows.reduce(
    (acc, r) => {
      if (r.type === "add") acc.added += 1;
      if (r.type === "del") acc.removed += 1;
      return acc;
    },
    { added: 0, removed: 0 }
  );

  return { hunks: toHunks(rows, context), stats, identical: false };
}

// Zip one hunk's rows into aligned left/right pairs for the split view.
// Context lines sit on both sides; a delete run and the add run following it
// are laid out side by side and padded with nulls when they differ in length.
export function toSplitRows(hunk) {
  const rows = hunk.rows;
  const out = [];
  let i = 0;

  while (i < rows.length) {
    const row = rows[i];
    if (row.type === "context") {
      out.push({ left: row, right: row });
      i += 1;
      continue;
    }

    let delEnd = i;
    while (delEnd < rows.length && rows[delEnd].type === "del") delEnd += 1;
    let addEnd = delEnd;
    while (addEnd < rows.length && rows[addEnd].type === "add") addEnd += 1;

    const dels = rows.slice(i, delEnd);
    const adds = rows.slice(delEnd, addEnd);
    for (let k = 0; k < Math.max(dels.length, adds.length); k += 1) {
      out.push({ left: dels[k] || null, right: adds[k] || null });
    }
    i = addEnd;
  }

  return out;
}
